import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiRequest } from '../../services/apiClient';
import { useMutation } from '../../hooks/useMutation';
import PasswordField from '../../components/auth/PasswordField';
import { PRODUCT_NAME } from '../../config/appBranding';
import Button from '../../components/ui/Button';
import FormField from '../../components/ui/FormField';
import Input from '../../components/ui/Input';
import '../../components/auth/LoginPage.css';
import './AuthForm.css';

const DEFAULT_WORKSPACE_SLUG = process.env.REACT_APP_DEFAULT_WORKSPACE_SLUG || (process.env.NODE_ENV === 'development' ? 'demo-company' : '');

function ResetPasswordPage() {
  const [params] = useSearchParams();
  const [workspaceSlug, setWorkspaceSlug] = useState(
    () => (params.get('workspace') || DEFAULT_WORKSPACE_SLUG).trim().toLowerCase()
  );
  const [token] = useState(() => params.get('token') || '');
  const [newPassword, setNewPassword] = useState('');
  const [validationError, setValidationError] = useState('');

  const {
    mutate: resetPassword,
    isLoading,
    error,
    successMessage
  } = useMutation(
    (payload) => apiRequest('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(payload)
    }),
    {
      successMessage: 'Your password has been reset. You can sign in with your new password.',
      errorFallback: 'Unable to reset password. Request a new reset link and try again.'
    }
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    const normalizedWorkspaceSlug = workspaceSlug.trim().toLowerCase();

    if (!normalizedWorkspaceSlug || !token || !newPassword) {
      setValidationError('Enter your company workspace and new password to continue.');
      return;
    }

    setValidationError('');
    await resetPassword({
      workspaceSlug: normalizedWorkspaceSlug,
      token,
      newPassword
    });
  };

  const displayedError = validationError || error;

  return (
    <div className="login-shell">
      <div className="login-panel">
        <div className="login-brand">
          <span className="login-eyebrow">Secure Reset</span>
          <h1>Create new {PRODUCT_NAME} password</h1>
          <p>Use the workspace from your reset email and choose a new password.</p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <FormField label="Company Workspace" htmlFor="workspace" required>
            <Input
              id="workspace"
              type="text"
              value={workspaceSlug}
              onChange={(event) => setWorkspaceSlug(event.target.value)}
              placeholder="your-workspace"
              autoComplete="organization"
              spellCheck="false"
              error={Boolean(validationError) && !workspaceSlug.trim()}
            />
          </FormField>

          <PasswordField
            id="new-password"
            label="New Password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            placeholder="Enter new password"
            autoComplete="new-password"
          />

          {displayedError ? (
            <div className="auth-form__alert auth-form__alert--error" role="alert">
              {displayedError}
            </div>
          ) : null}

          {successMessage ? (
            <div className="auth-form__alert auth-form__alert--success" role="status">
              {successMessage}
            </div>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            loading={isLoading}
            loadingLabel="Resetting…"
          >
            Reset password
          </Button>

          <p className="auth-form__help">
            <Link to="/login">Back to sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
